import React, { useState, useEffect, useCallback } from 'react';
import { Table, Input, Select, DatePicker, Button, Tag, Tooltip, message } from 'antd';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { getAuditLogs } from '../../api/logs';
import UserSearchSelect from '../common/UserSearchSelect';
import usePersistedPageSize from '../../hooks/usePersistedPageSize';
import useServerSort from '../../hooks/useServerSort';

const { Option } = Select;
const { RangePicker } = DatePicker;

const ACTION_COLORS = {
  created: 'green',
  updated: 'blue',
  deleted: 'red',
  restored: 'orange',
};

const ACTION_LABELS = {
  created: 'Oluşturuldu',
  updated: 'Güncellendi',
  deleted: 'Silindi',
  restored: 'Geri Alındı',
};

const AuditLogTab = () => {
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [persistedPageSize, setPersistedPageSize] = usePersistedPageSize('auditLogs', 20);
  const [pagination, setPagination] = useState({ current: 1, pageSize: persistedPageSize, total: 0 });
  const { sortField, sortOrder, applySorter } = useServerSort();

  const [filters, setFilters] = useState({
    user_id:    null,
    action:     null,
    model_type: null,
    date_from:  null,
    date_to:    null,
  });

  useEffect(() => {
    fetchLogs(1);
  }, []);

  const fetchLogs = useCallback(async (page = 1, perPage = pagination.pageSize, sortBy = sortField, sortDir = sortOrder ? (sortOrder === 'descend' ? 'desc' : 'asc') : null) => {
    setLoading(true);
    try {
      const res = await getAuditLogs({
        ...filters,
        page,
        per_page: perPage,
        sort_by: sortBy || undefined,
        sort_direction: sortDir || undefined,
      });
      setLogs(res.data.data || []);
      setPagination(p => ({ ...p, current: page, pageSize: perPage, total: res.data.total }));
    } catch {
      message.error('Denetim logları yüklenemedi.');
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.pageSize, sortField, sortOrder]);

  const columns = [
    {
      title: 'Tarih',
      dataIndex: 'created_at',
      width: 150,
      sorter: true,
      sortOrder: sortField === 'created_at' ? sortOrder : null,
      render: v => dayjs(v).format('DD.MM.YYYY HH:mm'),
    },
    {
      title: 'Kullanıcı',
      key: 'user',
      render: (_, r) => r.user ? (
        <Button type="link" style={{ padding: 0, height: 'auto' }} onClick={() => navigate(`/users/${r.user.id}`)}>
          {r.user.name} {r.user.surname}
        </Button>
      ) : '—',
    },
    {
      title: 'İşlem',
      dataIndex: 'action',
      width: 120,
      sorter: true,
      sortOrder: sortField === 'action' ? sortOrder : null,
      render: v => <Tag color={ACTION_COLORS[v] || 'default'}>{ACTION_LABELS[v] || v}</Tag>,
    },
    {
      title: 'Model',
      dataIndex: 'model_type',
      width: 130,
      sorter: true,
      sortOrder: sortField === 'model_type' ? sortOrder : null,
    },
    {
      title: 'Kayıt ID',
      dataIndex: 'model_id',
      width: 80,
      align: 'center',
    },
    {
      title: 'Değişiklikler',
      key: 'changes',
      render: (_, r) => {
        // Backend old_values/new_values olarak ayri iki alan donuyor (tek bir
        // "changes" alani yok) - action'a gore hangisinin anlamli oldugu
        // degisiyor: update'te new_values sadece degisen alanlari, old_values
        // ise tum eski kaydi tasiyor, digerlerinde tek taraf dolu oluyor.
        const oldValues = r.old_values || {};
        const newValues = r.new_values || {};
        const keys = r.action === 'deleted'
          ? Object.keys(oldValues)
          : Object.keys(newValues);

        if (keys.length === 0) return <span className="text-gray-400">—</span>;

        const summary = keys.slice(0, 3).join(', ') + (keys.length > 3 ? ` +${keys.length - 3}` : '');

        return (
          <Tooltip
            title={
              <div className="text-xs max-w-sm">
                {keys.map(k => (
                  <div key={k} className="mb-1">
                    <strong>{k}:</strong>{' '}
                    {r.action === 'updated'
                      ? <>{JSON.stringify(oldValues[k])} → {JSON.stringify(newValues[k])}</>
                      : JSON.stringify(r.action === 'deleted' ? oldValues[k] : newValues[k])}
                  </div>
                ))}
              </div>
            }
          >
            <span className="text-blue-500 cursor-pointer">{summary}</span>
          </Tooltip>
        );
      },
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap gap-3 mb-4">
        {/*
          SUNUCU TARAFLI ARAMA — eskiden `getUsers({ per_page: 100 })` ile ilk
          100 kullanıcı çekilip istemcide süzülüyordu. Sistemde 530+ veli var,
          yani aranan kişi çoğu zaman listede HİÇ YOKTU: 19.09.2026'da bir
          güvenlik uyarısı araştırılırken "Miraç Yılmaz" bu yüzden bulunamadı,
          ekran da neden bulunamadığını söylemiyordu.

          CLAUDE.md kuralı: yeni sınırsız/sabit select yazma, UserSearchSelect
          kullan.
        */}
        <UserSearchSelect
          allowClear
          style={{ width: 220 }}
          placeholder="Kullanıcı ara..."
          value={filters.user_id}
          onChange={v => setFilters(f => ({ ...f, user_id: v }))}
        />

        <Select
          allowClear
          placeholder="İşlem"
          style={{ width: 140 }}
          value={filters.action}
          onChange={v => setFilters(f => ({ ...f, action: v }))}
        >
          {Object.entries(ACTION_LABELS).map(([k, v]) => (
            <Option key={k} value={k}>{v}</Option>
          ))}
        </Select>

        <Input
          allowClear
          placeholder="Model tipi (örn: Student)"
          style={{ width: 200 }}
          value={filters.model_type}
          onChange={e => setFilters(f => ({ ...f, model_type: e.target.value || null }))}
        />

        <RangePicker
          format="DD.MM.YYYY"
          onChange={(_, strs) => setFilters(f => ({
            ...f,
            date_from: strs[0] ? dayjs(strs[0], 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
            date_to:   strs[1] ? dayjs(strs[1], 'DD.MM.YYYY').format('YYYY-MM-DD') : null,
          }))}
        />

        <Button type="primary" onClick={() => fetchLogs(1)}>Filtrele</Button>
      </div>

      <Table
        dataSource={logs}
        rowKey="id"
        columns={columns}
        loading={loading}
        size="small"
        onChange={(paginationInfo, _filters, sorter) => {
          const { sortField: sField, sortDirection: sDir } = applySorter(sorter);
          setPersistedPageSize(paginationInfo.pageSize);
          fetchLogs(paginationInfo.current, paginationInfo.pageSize, sField, sDir);
        }}
        pagination={{
          current: pagination.current,
          pageSize: pagination.pageSize,
          total: pagination.total,
          showSizeChanger: true,
          pageSizeOptions: ['20', '50', '100', '200'],
          showTotal: total => `Toplam ${total} kayıt`,
        }}
      />
    </div>
  );
};

export default AuditLogTab;
